import React, { useEffect, useId, useState } from 'react';
import { Search, Package, Calendar, DollarSign, User, X, Eye } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useOrdersList } from '@/lib/api/orders';
import { formatMoney } from "@/lib/money";
import { cn } from "@/lib/utils";

interface OrderSelectorProps {
  value: string;
  onChange: (value: string) => void;
  storeId: string;
  searchable?: boolean;
  placeholder?: string;
  allowPreviewMode?: boolean;
}

export function OrderSelector({
  value,
  onChange,
  storeId,
  searchable = true,
  placeholder = 'Search orders...',
  allowPreviewMode = false,
}: OrderSelectorProps) {
  const instanceId = useId();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: ordersData, isLoading } = useOrdersList({
    sortBy: { field: 'orders.createdAt', direction: 'desc' },
    pagination: { page: 1, pageSize: 20 },
    search: searchQuery || undefined,
    filters: {
      'stores.id': [storeId],
    },
  });

  const orders = ordersData?.orders || [];

  // Auto-select preview mode when no orders exist and preview mode is allowed
  useEffect(() => {
    if (allowPreviewMode && !isLoading && orders.length === 0 && value !== 'preview') {
      onChange('preview');
    }
  }, [allowPreviewMode, isLoading, orders.length, value, onChange]);

  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="animate-pulse">
          <div className="mb-2 h-9 rounded bg-gray-200" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse rounded-md border p-3">
            <div className="flex items-start gap-3">
              <div className="h-4 w-4 rounded bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-48 rounded bg-gray-200" />
                <div className="h-3 w-64 rounded bg-gray-200" />
                <div className="h-3 w-32 rounded bg-gray-200" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {searchable && (
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
          <Input
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 pl-8 pr-8 text-sm"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="text-muted-foreground hover:text-foreground absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {searchQuery && orders.length > 0 && (
        <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <Package className="h-3.5 w-3.5" />
          <span>
            {orders.length} {orders.length === 1 ? 'order' : 'orders'}
          </span>
        </div>
      )}

      <RadioGroup name={`order-selector-${instanceId}`} value={value} onValueChange={onChange}>
        <div className="h-[400px] overflow-y-auto py-1 pl-1 pr-4">
          <div className="space-y-3">
            {orders.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8">
                {allowPreviewMode && !searchQuery ? (
                  <>
                    <Eye className="text-muted-foreground mb-2 h-8 w-8" />
                    <p className="text-sm font-medium">Preview mode — no orders available</p>
                    <p className="text-muted-foreground mt-1 text-xs">
                      The page will be previewed without order data.
                    </p>
                  </>
                ) : (
                  <>
                    <Package className="text-muted-foreground mb-2 h-8 w-8" />
                    <p className="text-muted-foreground text-sm">
                      {searchQuery ? `No orders found matching "${searchQuery}"` : 'No orders available'}
                    </p>
                  </>
                )}
              </div>
            ) : (
              orders.map((order) => (
                <label
                  key={order.id}
                  className={cn(
                    'block cursor-pointer rounded-lg border transition-all',
                    'hover:border-primary/50 hover:bg-accent',
                    value === order.id && 'border-primary bg-accent ring-primary/20 ring-1',
                  )}
                >
                  <div className="p-2.5">
                    <div className="flex items-start gap-2.5">
                      <RadioGroupItem value={order.id} className="shrink-0" />

                      <div className="min-w-0 flex-1">
                        {/* Order Number + Status */}
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium">
                              #{order.orderNumber || order.id.slice(-8)}
                            </span>
                            <Badge
                              variant={
                                order.status === 'paid'
                                  ? 'green'
                                  : order.status === 'open'
                                    ? 'yellow'
                                    : order.status === 'failed'
                                      ? 'red'
                                      : 'gray'
                              }
                              className="text-[10px]"
                            >
                              {order.status}
                            </Badge>
                          </div>
                        </div>

                        <div className="text-muted-foreground space-y-0.5 text-xs">
                          {/* Customer */}
                          {order.customer && (
                            <p className="flex items-center gap-1.5">
                              <User className="h-3 w-3 shrink-0" />
                              <span className="truncate">
                                {[order.customer.firstName, order.customer.lastName]
                                  .filter(Boolean)
                                  .join(' ') || order.customer.email}
                              </span>
                            </p>
                          )}

                          {/* Amount + Date */}
                          <div className="flex items-center gap-3">
                            <p className="flex items-center gap-1">
                              <DollarSign className="h-3 w-3 shrink-0" />
                              <span className="font-medium">
                                {formatMoney(
                                  order.paidAmount || order.summaries?.[0]?.totalAdjustedAmount || 0,
                                  order.currency || order.summaries?.[0]?.currency || 'EUR',
                                )}
                              </span>
                            </p>
                            <p className="flex items-center gap-1">
                              <Calendar className="h-3 w-3 shrink-0" />
                              <span>{formatDate(order.createdAt)}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>
      </RadioGroup>
    </div>
  );
}
